import {
  LoggedInUserInfoBaseDTO,
  OrganizationDTO,
  UserDTO,
} from 'dtos';
import {
  EMRTypes,
} from 'dtos/eMRTypes';
import {
  ExternalUserInfoDTO,
} from 'dtos/externalUserInfoDTO';
import {
  ModMedOrganizationInfoDTO,
} from 'dtos/modMedOrganizationInfoDTO';
import {
  ChartTab,
  DEFAULT_CHART_TAB,
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import SETTINGS_PATHS from 'views/EMR/Settings/paths';

export type LoggedInUserInfoAccess = Omit<LoggedInUserInfoBaseDTO, 'user' | 'organization'> & {
  user?: Partial<UserDTO & ExternalUserInfoDTO>;
  organization?: Partial<OrganizationDTO & ModMedOrganizationInfoDTO> & {
    displayName?: string | null;
  };
};

type UserInfo = LoggedInUserInfoAccess | null | undefined;

export const EMR_PATHS = {
  ENCOUNTERS: '/encounters',
  FORMS: '/forms',
  CHARTS: '/charts',
  PROFILE: '/profile',
  PCC: '/pcc',
} as const;

export const DASHBOARD_PATHS = {
  ROOT: '/pcc',
  OVERVIEW: '/pcc/overview',
  RTM: '/pcc/rtm',
  TEMPO: '/pcc/tempo',
  CCM: '/pcc/ccm',
} as const;

export const DEFAULT_EMR_PATH = EMR_PATHS.ENCOUNTERS;
export const DEFAULT_SETTINGS_PATH = SETTINGS_PATHS.APPOINTMENTS;
export const LIMITED_ACCESS_DEFAULT_PATH = EMR_PATHS.PCC;

// Marks an org as unrestricted for routes or chart tabs.
const ALL = 'all';

type OrgAccess = {
  // Allowed path prefixes, excluding chart paths (see chartTabs).
  routes: string[] | typeof ALL;
  chartTabs: ChartTab[] | typeof ALL;
  defaultPath: string;
  defaultSettingsPath: string;
  features: {
    isPCCActionAvailable: boolean;
    canUseRecentSearches: boolean;
    canRequestMultimedia: boolean;
    requiresRTMEnrollmentDetails: boolean;
    // Also gated by the matching user flag.
    canSendTempoCommunique: boolean;
    canUseCCMAgent: boolean;
    canUseRTMMonitoring: boolean;
  };
};

const FULL_ACCESS: OrgAccess = {
  routes: ALL,
  chartTabs: ALL,
  defaultPath: DEFAULT_EMR_PATH,
  defaultSettingsPath: DEFAULT_SETTINGS_PATH,
  features: {
    isPCCActionAvailable: true,
    canUseRecentSearches: true,
    canRequestMultimedia: true,
    requiresRTMEnrollmentDetails: false,
    canSendTempoCommunique: false,
    canUseCCMAgent: false,
    canUseRTMMonitoring: false,
  },
};

const ORG_ACCESS: Partial<Record<EMRTypes, OrgAccess>> = {
  [EMRTypes.ModMed]: {
    routes: [
      SETTINGS_PATHS.FORMS,
      EMR_PATHS.FORMS,
      EMR_PATHS.ENCOUNTERS,
      EMR_PATHS.PROFILE,
      DASHBOARD_PATHS.ROOT,
    ],
    chartTabs: ['pcc'],
    defaultPath: LIMITED_ACCESS_DEFAULT_PATH,
    defaultSettingsPath: SETTINGS_PATHS.FORMS,
    features: {
      isPCCActionAvailable: false,
      canUseRecentSearches: false,
      canRequestMultimedia: false,
      requiresRTMEnrollmentDetails: false,
      canSendTempoCommunique: true,
      canUseCCMAgent: true,
      canUseRTMMonitoring: true,
    },
  },
  [EMRTypes.Populate]: {
    ...FULL_ACCESS,
    features: {
      ...FULL_ACCESS.features,
      requiresRTMEnrollmentDetails: true,
    },
  },
};

function normalizePath(pathname: string): string {
  if (pathname !== '/' && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

type ChartPathDetails = {
  patientId: string;
  tab: string | null;
};

function getChartPathDetails(pathname: string): ChartPathDetails | null {
  const parts = normalizePath(pathname).split('/').filter(Boolean);
  const hasValidChartPathLength = parts.length === 2 || parts.length === 3;

  if (`/${parts[0]}` !== EMR_PATHS.CHARTS || !hasValidChartPathLength) {
    return null;
  }

  return {
    patientId: parts[1] ?? '',
    tab: parts[2] ?? null,
  };
}

// Components should use the `useEMRAccess` hook; this is exported for the hook and tests.
export function getEMRAccess(userInfo: UserInfo) {
  const org = (userInfo?.emrType && ORG_ACCESS[userInfo.emrType]) || FULL_ACCESS;
  const {
    routes,
    chartTabs,
    features,
    defaultPath,
    defaultSettingsPath,
  } = org;
  const flags = userInfo?.flags;

  const defaultChartTab = chartTabs === ALL ? DEFAULT_CHART_TAB : chartTabs[0] ?? DEFAULT_CHART_TAB;
  const hasChartAccess = chartTabs === ALL || chartTabs.length > 0;

  const canChartTab = (tab: ChartTab): boolean => (
    chartTabs === ALL || chartTabs.includes(tab)
  );

  const getDefaultChartPath = (patientId?: number | string | null): string | undefined => (
    getPatientChartPath(
      typeof patientId === 'string' ? Number(patientId) : patientId,
      defaultChartTab,
    )
  );

  const canPath = (pathname: string): boolean => {
    if (routes === ALL) {
      return true;
    }

    const chartPathDetails = getChartPathDetails(pathname);

    if (chartPathDetails !== null) {
      return canChartTab((chartPathDetails.tab ?? defaultChartTab) as ChartTab);
    }

    const path = normalizePath(pathname);

    // Ancestors of an allowed route (e.g. `/settings`) are allowed so they can redirect to it.
    return routes.some((route) => (
      path === route
      || path.startsWith(`${route}/`)
      || route.startsWith(`${path}/`)
    ));
  };

  const getAccessiblePath = (pathname: string): string => {
    const chartPathDetails = getChartPathDetails(pathname);

    if (chartPathDetails !== null && hasChartAccess) {
      return getDefaultChartPath(chartPathDetails.patientId) ?? defaultPath;
    }

    return defaultPath;
  };

  return {
    ...features,
    canSendTempoCommunique: features.canSendTempoCommunique
      && (flags?.maySendTempoCommunique ?? false),
    canUseCCMAgent: features.canUseCCMAgent
      && (flags?.agents?.ccmAgentEnabled ?? false),
    canUseRTMMonitoring: features.canUseRTMMonitoring
      && (flags?.isRTMEnabled ?? false),
    isRestricted: routes !== ALL,
    hasChartAccess,
    canPath,
    canChartTab,
    getAccessiblePath,
    defaultChartTab,
    getDefaultChartPath,
    defaultPath,
    defaultSettingsPath,
    userInfo: userInfo ?? null,
  };
}

export type EMRAccess = ReturnType<typeof getEMRAccess>;
